import { difference, sortBy } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { ColumnDescriptor } from './types';

const COLUMN_FIELDS_WITH_CORESPONDING_FIELD_IDS = new Set([
  'function_id',
  'business_unit_id',
  'department_id',
  'cost_center_id',
]);

// This method is used to filter out columns that have both id and description fields
// and display as default the preferred, leaving the other as an option to be selected
export const filterColumnsWithBothIdAndDescriptionFields = (
  columns: ColumnDescriptor[]
) =>
  columns.filter(
    (column) =>
      COLUMN_FIELDS_WITH_CORESPONDING_FIELD_IDS.has(column.field) &&
      columns.some((c) => c.field === column.field.replace('_id$', ''))
  );

// The fields a selector should start with unticked: the id duplicates above,
// plus whatever the caller nominates. Shared so the three selectors that need
// it can't drift apart.
export const defaultUnselectedFields = (
  columns: ColumnDescriptor[],
  extraUnselected: string[] = []
): string[] =>
  Array.from(
    new Set([
      ...filterColumnsWithBothIdAndDescriptionFields(columns).map(
        (column) => column.field
      ),
      ...extraUnselected,
    ])
  );

export const useFilterableTableColumnSelectorModal = ({
  columns,
  disabled = false,
  defaultUnselectedColumns = [],
  allowedColumns,
  onColumnsChange,
}: {
  columns: ColumnDescriptor[];
  disabled?: boolean;
  defaultUnselectedColumns?: string[];
  allowedColumns?: string[];
  onColumnsChange?: (visibleColumns: string[]) => void;
}) => {
  const calaculateVisibleColumns = useCallback(
    (
      hideColumnSelector: boolean,
      columnsForVisualDisplayUnfiltered: ColumnDescriptor[]
    ): string[] | undefined => {
      return hideColumnSelector
        ? undefined
        : columnsForVisualDisplayUnfiltered.map((c) => c.field);
    },
    []
  );

  // Despite the name, this one is filtered - just by what is *selectable*
  // rather than by what is selected. It is the menu the column selector
  // offers, so it has to include columns the user has currently hidden.
  // columnsForVisualDisplay below is the subset the table actually draws.
  const columnsForVisualDisplayUnfiltered = useMemo(() => {
    const selectable = columns.filter(
      (c: ColumnDescriptor) =>
        !c.csvOnly &&
        !c.filterOnly &&
        (!allowedColumns?.length || !!allowedColumns.find((x) => x == c.field))
    );

    // when a caller supplies allowedColumns, its order is the column order
    return allowedColumns?.length
      ? sortBy(selectable, (c) => allowedColumns.indexOf(c.field))
      : selectable;
  }, [columns, allowedColumns]);

  const [visibleColumns, setVisibleColumns] = useState<string[] | undefined>(
    () => calaculateVisibleColumns(disabled, columnsForVisualDisplayUnfiltered)
  );

  const [unselectedColumnsMemory, setUnselectedColumnsMemory] = useState<
    string[]
  >(
    defaultUnselectedFields(
      columnsForVisualDisplayUnfiltered,
      defaultUnselectedColumns
    )
  );

  const [showColumnSelector, setShowColumnSelector] = useState<boolean>(false);

  useEffect(() => {
    setVisibleColumns(
      calaculateVisibleColumns(disabled, columnsForVisualDisplayUnfiltered)
    );
  }, [disabled, columnsForVisualDisplayUnfiltered, calaculateVisibleColumns]);

  useEffect(() => {
    if (onColumnsChange && visibleColumns) {
      onColumnsChange(visibleColumns);
    }
  }, [visibleColumns, onColumnsChange]);

  // What the table draws: the selectable columns above, minus the ones the
  // user unticked, in the order the selector put them in.
  const columnsForVisualDisplay = useMemo(() => {
    const shown = columnsForVisualDisplayUnfiltered.filter(
      (c) =>
        (!visibleColumns || visibleColumns.includes(c.field)) &&
        !unselectedColumnsMemory.includes(c.field)
    );

    // the selector emits its fields in display order, so that order wins over
    // the order the column descriptors were generated in
    return visibleColumns?.length
      ? sortBy(shown, (c) => visibleColumns.indexOf(c.field))
      : shown;
  }, [
    columnsForVisualDisplayUnfiltered,
    visibleColumns,
    unselectedColumnsMemory,
  ]);

  const handleToggleColumnSelector = useCallback(
    () => setShowColumnSelector(false),
    []
  );

  const handleOpenColumnSelector = useCallback(
    () => setShowColumnSelector(true),
    []
  );

  const handleCallbackColumnSelector = useCallback(
    (selectedColumns: string[], unselectedColumns: string[]) => {
      setUnselectedColumnsMemory(unselectedColumns);
      setVisibleColumns(difference(selectedColumns, unselectedColumns));
    },
    []
  );

  return {
    columnsForVisualDisplay,
    columnsForVisualDisplayUnfiltered,
    showColumnSelector,
    visibleColumns,
    handleOpenColumnSelector,
    handleToggleColumnSelector,
    handleCallbackColumnSelector,
  };
};
